/**
 * Branch context — which branch a person is working in.
 *
 * The branch id is mirrored into apiFetch so every request carries X-Branch-Id.
 * The NATION exposed here is for UI gating only; the server resolves nation
 * itself from its own branch registry and never trusts a client-sent nation.
 *
 * WHICH BRANCH THAT IS:
 *
 *   - one open branch: that branch. There is nothing to choose, so nobody is
 *     asked, and the shell shows no selector.
 *   - several: the one picked in the shell's selector (layout/BranchSwitcher),
 *     kept in localStorage so it survives a reload.
 *   - several and none picked, or no branches registered: no branch. The UI
 *     and the server both fall back to the company's own region
 *     (tenant.defaultNation; ENG only until it has loaded).
 *
 * NOBODY COULD PICK ONE UNTIL 2026-10-04. This file was written as "the
 * platform's first branch selector" and held the setter, but no screen called
 * it and a single-branch tenant was never placed in its branch. Every user was
 * therefore in "no branch": shown England's modules and refused Wales's, by
 * the gates here and by the server. branchContext.test.tsx reproduces each
 * part of that.
 *
 * THE HEADER IS SET BEFORE THE FIRST REQUEST, NOT AFTER THE FIRST EFFECT. It
 * used to be set in a passive effect of this provider. A page's own first
 * request is made from an effect further down the tree, and those run first,
 * so after a reload the opening request of every screen went out with no
 * branch at all and was answered for the tenant's default nation. Measured,
 * not reasoned: "is already on the FIRST request a page makes after a reload"
 * failed against that code with the header absent.
 */
import {
  createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchBranches, type BranchPublic, type Nation } from './branchesApi';
import { useCompanyRegion } from './companyMoney';
import { setActiveBranchId } from './api';

const STORAGE_KEY = 'jeanieiq.activeBranchId';

interface BranchContextValue {
  branches: BranchPublic[];
  branchId: string | null;
  nation: Nation;
  setBranchId: (id: string | null) => void;
}

const Ctx = createContext<BranchContextValue | null>(null);

export function BranchProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();

  // The branch somebody picked. Read once, and handed to apiFetch in the same
  // breath so the stored choice is on the very first request (see the header).
  const [chosenId, setChosenIdState] = useState<string | null>(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    setActiveBranchId(stored);
    return stored;
  });

  const q = useQuery<BranchPublic[]>({
    queryKey: ['branches'],
    queryFn: ({ signal }) => fetchBranches(signal),
  });
  const branches = useMemo(() => (q.data ?? []).filter((b) => b.isActive), [q.data]);

  // One open branch is the branch; a choice only exists when there are several.
  const only = branches.length === 1 ? branches[0] : undefined;
  const branchId = only ? only.id : chosenId;

  /*
   * Keep apiFetch in step, and ask every open screen again when the branch
   * changes. A layout effect, because it must land before the effects of the
   * screens below it, which is where their requests are made. Without the
   * refetch a list loaded for one nation stayed on screen under the other
   * until something else happened to reload it. The registry itself is left
   * alone: it does not depend on the branch, and refetching it here would only
   * re-run this.
   */
  const sentId = useRef<string | null>(chosenId);
  useLayoutEffect(() => {
    if (sentId.current === branchId) return;
    sentId.current = branchId;
    setActiveBranchId(branchId);
    void queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] !== 'branches' });
  }, [branchId, queryClient]);

  // A stored id that no longer exists - branch deactivated, or a different
  // tenant's id left over from a previous login - is dropped rather than sent.
  useEffect(() => {
    if (chosenId && branches.length > 0 && !branches.some((b) => b.id === chosenId)) {
      setChosenIdState(null);
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, [chosenId, branches]);

  const setBranchId = (id: string | null): void => {
    setChosenIdState(id);
    if (id) window.localStorage.setItem(STORAGE_KEY, id);
    else window.localStorage.removeItem(STORAGE_KEY);
  };

  const companyRegion = useCompanyRegion();
  const nation: Nation = branches.find((b) => b.id === branchId)?.nation ?? (companyRegion as Nation | null) ?? 'ENG';

  const value = useMemo(
    () => ({ branches, branchId, nation, setBranchId }),
    [branches, branchId, nation],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBranch(): BranchContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useBranch must be used inside <BranchProvider>');
  return v;
}

/**
 * The same, for something drawn both inside the signed-in shell and outside it.
 * The page header is one: the portal and kiosk screens render it with no
 * provider above them, and there it has no branch to offer rather than a
 * reason to throw.
 */
export function useBranchIfAny(): BranchContextValue | null {
  return useContext(Ctx);
}
